import { ArrowForwardIos } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useService from "../../../../hooks/useService.js";
import DrawerFiltersListLocal from "../../../cm/Dashboard/FullPages/Reports/components/ReportsDetail/components/DrawerFiltersListLocal.jsx";
import Title from "../../../cm/Dashboard/FullPages/Reports/components/ReportsDetail/components/Title.jsx";
import ReportDetail from "../../../cm/Dashboard/FullPages/Reports/components/ReportsDetail/index.jsx";
import { transformFilters } from "../../../cm/Dashboard/FullPages/Reports/components/ReportsDetail/utils/dto.js";
import ReportTooltip from "../../../cm/Dashboard/FullPages/Reports/components/ReportsList/components/ReportTooltip.jsx";
import CustomIconButton from "../../../ui library/components/buttons/CustomIconButton.js";
import HNSColors from "../../../ui library/HNSColors.js";
import { FiltersButton } from "../../drawer-filters/components/FiltersButton.jsx";
import { useDrawerFiltersState } from "../../drawer-filters/hooks/useDrawerFiltersState.js";
import AccordionContainer from "../../FollowUps/followup-form/modules/DynamicSection/components/AccordionContainer.jsx";
import { ACCORDION_DETAILS_SX } from "../helpers/styles.js";

// ============================ Render details of each Reports ============================
const normalizeFilters = (filters) => {
  if (filters instanceof URLSearchParams) {
    return new URLSearchParams(filters);
  }
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    if (Array.isArray(value)) {
      params.set(key, value.join(","));
    } else {
      params.set(key, String(value));
    }
  });
  return params;
};

const toPlainObject = (filters) => {
  if (filters instanceof URLSearchParams) {
    return Object.fromEntries(filters.entries());
  }
  return filters || {};
};

const getRawFilterDefinitions = (response) => {
  if (Array.isArray(response)) {
    return response.map((record) => record?.raw ?? record);
  }
  return response?.raw ?? response ?? [];
};

const isEmptyFilterValue = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const applyFilterValue = (params, key, value) => {
  const rawKey = String(key);
  const baseKey = rawKey.replace(/(__in)$/, "");
  const targetKey =
    Array.isArray(value) && value.length > 1 && !rawKey.endsWith("__in")
      ? `${baseKey}__in`
      : rawKey;

  if (isEmptyFilterValue(value)) {
    params.delete(rawKey);
    params.delete(baseKey);
    params.delete(`${baseKey}__in`);
    return;
  }

  if (Array.isArray(value)) {
    params.delete(targetKey.endsWith("__in") ? baseKey : `${baseKey}__in`);
    params.set(targetKey, value.join(","));
    return;
  }

  params.delete(`${baseKey}__in`);
  params.set(targetKey, String(value));
};

const REPORT_ACCORDION_DETAILS_SX = {
  ...ACCORDION_DETAILS_SX,
  "& .MuiTableContainer-root": {
    overflow: "visible",
  },
  "& .MuiTableHead-root": {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: HNSColors.neutrals[900],
  },
  "& .MuiTableHead-root::before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: -16,
    height: 16,
    backgroundColor: HNSColors.neutrals[900],
    pointerEvents: "none",
  },
  "& .MuiTableCell-stickyHeader": {
    top: 0,
    zIndex: 11,
    backgroundColor: HNSColors.neutrals[900],
  },
};

const ANALYTICS_FILTER_DATE_PICKER_SLOT_PROPS = {
  textField: { variant: "outlined" },
  popper: {
    sx: {
      zIndex: (theme) => theme.zIndex.modal + 101,
    },
  },
  dialog: {
    sx: {
      zIndex: (theme) => theme.zIndex.modal + 101,
    },
  },
};

const AnalyticsReportFilterDrawer = ({
  filters,
  filtersUrl,
  updateFilter,
  bulkUpdateFilters,
  triggerId,
  closeButtonId,
}) => {
  const analyticsChartRepository = useService("repositories/analytics-chart-repository");
  const [open, setOpen] = useState(false);
  const [filterConfig, setFilterConfig] = useState([]);
  const [draftFilters, setDraftFilters] = useState(() => new URLSearchParams(filters));
  const [loading, setLoading] = useState(false);

  const activeDrawerFilters = draftFilters ?? filters;
  const { filterState } = useDrawerFiltersState({
    filterConfig,
    filters: activeDrawerFilters,
  });
  const modifiableFilterFieldNames = useMemo(
    () => filterState.map((filter) => filter.fieldName).filter(Boolean),
    [filterState]
  );

  const loadFilters = useCallback(async () => {
    setDraftFilters(new URLSearchParams(filters));
    if (!filtersUrl || filterConfig.length > 0 || loading) {
      return;
    }

    setLoading(true);
    try {
      const response = await analyticsChartRepository.fetchByUrl(filtersUrl);
      setFilterConfig(transformFilters(getRawFilterDefinitions(response)));
    } finally {
      setLoading(false);
    }
  }, [analyticsChartRepository, filterConfig.length, filters, filtersUrl, loading]);

  const openDrawer = useCallback(() => {
    loadFilters();
    setOpen(true);
  }, [loadFilters]);

  const updateDraftFilter = useCallback(
    (key, value, bulkNewFilters = null, notApplyOffset = false) => {
      const next = bulkNewFilters || new URLSearchParams(activeDrawerFilters);
      updateFilter(key, value, next, notApplyOffset);

      if (!bulkNewFilters) {
        setDraftFilters(new URLSearchParams(next));
      }
    },
    [activeDrawerFilters, updateFilter]
  );

  const bulkUpdateDraftFilters = useCallback(
    (filterMap) => {
      const next = new URLSearchParams(activeDrawerFilters);
      for (const [filterKey, filterValue] of filterMap) {
        updateFilter(filterKey, filterValue, next);
      }
      setDraftFilters(new URLSearchParams(next));
    },
    [activeDrawerFilters, updateFilter]
  );

  const hasPendingDrawerChanges = useMemo(() => {
    const keySet = new Set([...filters.keys(), ...activeDrawerFilters.keys()]);
    for (const key of keySet) {
      if ((filters.get(key) || null) !== (activeDrawerFilters.get(key) || null)) {
        return true;
      }
    }
    return false;
  }, [activeDrawerFilters, filters]);

  const applyDraftFilters = useCallback(() => {
    const changes = new Map();
    const keySet = new Set([...filters.keys(), ...activeDrawerFilters.keys()]);

    for (const key of keySet) {
      const currentValue = filters.get(key) || null;
      const nextValue = activeDrawerFilters.get(key) || null;

      if (currentValue !== nextValue) {
        let processedNextValue = nextValue;
        if (typeof nextValue === "string" && nextValue.includes(",") && key !== "columns") {
          processedNextValue = nextValue.split(",");
        }
        changes.set(key, processedNextValue);
      }
    }

    if (changes.size) {
      bulkUpdateFilters(changes);
    }
    setOpen(false);
  }, [activeDrawerFilters, bulkUpdateFilters, filters]);

  const clearDraftModifiableFilters = useCallback(() => {
    const next = new URLSearchParams(activeDrawerFilters);
    const keysToDelete = [];

    for (const key of next.keys()) {
      const shouldDelete = modifiableFilterFieldNames.some(
        (fieldName) => key === fieldName || key.startsWith(`${fieldName}__`)
      );

      if (shouldDelete) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => next.delete(key));
    setDraftFilters(next);
  }, [activeDrawerFilters, modifiableFilterFieldNames]);

  const hasClearableDraftValues = useMemo(() => {
    for (const key of activeDrawerFilters.keys()) {
      const isModifiableFieldKey = modifiableFilterFieldNames.some(
        (fieldName) => key === fieldName || key.startsWith(`${fieldName}__`)
      );

      if (isModifiableFieldKey) {
        return true;
      }
    }

    return false;
  }, [activeDrawerFilters, modifiableFilterFieldNames]);

  if (!filtersUrl) {
    return null;
  }

  return (
    <>
      <FiltersButton id={triggerId} onClick={openDrawer} sx={{ height: "32px", px: 2 }}>
        Filters
      </FiltersButton>
      <aside
        role="dialog"
        aria-label="Filter panel"
        className={`fixed left-0 top-0 z-[1400] flex h-screen w-[450px] transform flex-col rounded-br-[10px] rounded-tr-[10px] border-r border-[#ececec] bg-white shadow-[4px_0_12px_-2px_rgba(0,0,0,0.25)] transition-transform duration-300 ease-out will-change-transform ${open ? "translate-x-0" : "-translate-x-full"}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <Typography className="p-2 pl-4 text-lg font-bold">Filters</Typography>
          <CustomIconButton
            tooltipText="Hide Filter Menu"
            classes="rotate-180 p-2"
            handleOnClick={() => setOpen(false)}
            iconVisible={true}
            buttonId={closeButtonId}
            CustomIcon={<ArrowForwardIos className="text-sky-600" />}
          />
        </div>
        <Divider />
        <div className="flex min-h-0 flex-1 flex-col bg-white">
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <div className="flex h-12 items-center gap-2 text-sm font-medium">
              <span>Filters</span>
              {loading && <CircularProgress size={14} />}
            </div>
          </Box>
          <div className="min-h-0 flex-1 overflow-hidden">
            <DrawerFiltersListLocal
              datePickerSlotProps={ANALYTICS_FILTER_DATE_PICKER_SLOT_PROPS}
              filterState={filterState}
              updateFilter={updateDraftFilter}
              bulkUpdateFilters={bulkUpdateDraftFilters}
            />
          </div>
          <div className="border-t border-gray-200 p-3">
            <div className="flex gap-2">
              <Button
                fullWidth
                variant="outlined"
                onClick={clearDraftModifiableFilters}
                disabled={!hasClearableDraftValues}
              >
                Clear
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={applyDraftFilters}
                disabled={!hasPendingDrawerChanges}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const AnalyticReportSection = ({ section, sectionIndex }) => {
  const tabularReportRepository = useService("repositories/tabular-report-repository");
  const apiUrl = section.results;
  const filtersUrl = section.filters;
  const initialFilters = section.initialFilters || section.initial_filters || {};
  const initialFiltersKey = JSON.stringify(initialFilters || {});
  const initialFilterParams = useMemo(
    () => normalizeFilters(JSON.parse(initialFiltersKey)),
    [initialFiltersKey]
  );
  const reportId = useMemo(() => {
    if (!apiUrl) {
      return null;
    }
    const validApi = apiUrl.match(/\/(\d+)\//);
    return validApi ? validApi[1] : null;
  }, [apiUrl]);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState(() => new URLSearchParams(initialFilterParams));
  const latestRequestKeyRef = useRef(null);
  const pendingRequestKeyRef = useRef(null);
  const skipNextRequestKeyRef = useRef(null);
  const reportTitle = section?.display_name ?? `Section ${sectionIndex + 1}`;

  const filtersKey = useMemo(() => filters.toString(), [filters]);

  const buildEffectiveFilters = useCallback((filterParams, data) => {
    const nextFilters = new URLSearchParams(filterParams);
    if (!nextFilters.get("limit")) {
      nextFilters.set("limit", String(data?.defaultLimit || 100));
    }
    if (!nextFilters.get("offset")) {
      nextFilters.set("offset", "0");
    }
    return nextFilters;
  }, []);

  const loadData = useCallback(
    async (id, filterParams, requestKey) => {
      if (id) {
        try {
          const resp = await tabularReportRepository.findRecord(id, toPlainObject(filterParams));
          if (latestRequestKeyRef.current === requestKey) {
            const effectiveFilters = buildEffectiveFilters(filterParams, resp);
            if (effectiveFilters.toString() !== filterParams.toString()) {
              skipNextRequestKeyRef.current = `${id}:${effectiveFilters.toString()}`;
              setFilters(effectiveFilters);
            }
            setReportData(resp);
          }
        } catch (error) {
          if (latestRequestKeyRef.current === requestKey) {
            setReportData(null);
          }
          return error;
        } finally {
          if (pendingRequestKeyRef.current === requestKey) {
            pendingRequestKeyRef.current = null;
          }
        }
      }
    },
    [buildEffectiveFilters, tabularReportRepository]
  );

  function updateFilter(key, value, bulkNewFilters = null, notApplyOffset = false) {
    const nextFilters = bulkNewFilters || new URLSearchParams(filters);
    if (key !== "offset" && !notApplyOffset) {
      nextFilters.set("offset", "0");
    }

    if (key === "columns" || key === "limit" || key === "offset") {
      if (isEmptyFilterValue(value)) {
        nextFilters.delete(key);
      } else if (Array.isArray(value)) {
        nextFilters.set(key, value.join(","));
      } else {
        nextFilters.set(key, String(value));
      }
    } else {
      applyFilterValue(nextFilters, key, value);
    }

    if (!bulkNewFilters) {
      setFilters(new URLSearchParams(nextFilters));
    }
  }

  function bulkUpdateFilters(filterMap) {
    const nextFilters = new URLSearchParams(filters);
    for (const [filterKey, filterValue] of filterMap) {
      updateFilter(filterKey, filterValue, nextFilters);
    }
    setFilters(nextFilters);
  }

  useEffect(() => {
    setFilters(new URLSearchParams(initialFilterParams));
  }, [initialFilterParams]);

  useEffect(() => {
    if (!reportId) {
      return;
    }
    const requestKey = `${reportId}:${filtersKey}`;
    latestRequestKeyRef.current = requestKey;
    if (skipNextRequestKeyRef.current === requestKey) {
      skipNextRequestKeyRef.current = null;
      return;
    }
    if (pendingRequestKeyRef.current === requestKey) {
      return;
    }
    pendingRequestKeyRef.current = requestKey;
    loadData(reportId, new URLSearchParams(filtersKey), requestKey);
  }, [filtersKey, loadData, reportId]);

  const renderReportHeader = (includeFilters = false) => (
    <div className="flex flex-wrap items-center gap-2">
      <Title>{reportTitle}</Title>
      <ReportTooltip description={section?.description} allowHover />
      {includeFilters && filtersUrl && (
        <div
          className="ml-2"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
        >
          <AnalyticsReportFilterDrawer
            filters={filters}
            filtersUrl={filtersUrl}
            updateFilter={updateFilter}
            bulkUpdateFilters={bulkUpdateFilters}
            triggerId={`analytics-report-filters-button-${sectionIndex}`}
            closeButtonId={`analytics-report-filters-close-button-${sectionIndex}`}
          />
        </div>
      )}
    </div>
  );

  if (!reportData) {
    return (
      <AccordionContainer
        key={section?.display_name ?? sectionIndex}
        title={renderReportHeader(false)}
        accordionDetailsSx={ACCORDION_DETAILS_SX}
      />
    );
  }

  return (
    <AccordionContainer
      key={section?.display_name ?? sectionIndex}
      title={renderReportHeader(true)}
      accordionDetailsSx={REPORT_ACCORDION_DETAILS_SX}
    >
      <ReportDetail
        bulkUpdateFilters={bulkUpdateFilters}
        filters={filters}
        filtersUrl={filtersUrl}
        reportData={reportData}
        showActions={false}
        showFilters={false}
        showSearchBar={false}
        showTitle={false}
        setFilters={setFilters}
        syncQueryParams={false}
        updateFilter={updateFilter}
        reportId={reportId}
      />
    </AccordionContainer>
  );
};

// ============================ Render main Reports Section component ============================
export default function AnalyticsReportsSection({ reportsSection }) {
  if (!reportsSection || reportsSection.length === 0) {
    return null;
  }

  return (
    <>
      {reportsSection.map((section, sectionIndex) => (
        <AnalyticReportSection
          key={section?.display_name ?? sectionIndex}
          section={section}
          sectionIndex={sectionIndex}
        />
      ))}
    </>
  );
}
